/** @module Apparel/ProductColorMixin */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"csi/config",
	"csi/def/Action",
	"csi/def/ViewRef",
	"csi/xml",
	"csi/xml/Query",
	"./ProductType"
], function(declare, lang, config, Action, ViewRef, xml, Query, ProductType) {

	return declare(null, {
		FOOD_BOMLINE_ITEM: "FoodBOMLineItem",
		PART_MATERIAL: "PartMaterial",

		/**
		 * Return action to select a ProductColor from Product in context
		 * @param {Context} ctx
		 * @return {Action|null}
		 */
		_getProductColorsAction: function(ctx) {
			var product = this._getProduct(ctx);
			if (product) {
				const q = new Query().Attr("Active", xml.OP_EQUALS, true);
				this._applySeasonAvailabilityFilters(ctx, q);
				return lang.mixin(new Action(), {
					m_fixedQuery: q,
					m_viewRef: lang.mixin(new ViewRef(), {
						m_id: "ProductColors",
						m_url: product.$URL
					})
				});
			}

			return null;
		},

		/**
		 * Return action to select a ColorSpecification for Materials
		 * @param {Context} ctx
		 * @return {Action}
		 */
		_getMaterialColorSpecsAction: function(ctx) {
			const q = new Query().Attr(this._isPMActualAStyle(ctx) ? "OKForStyle" : "OKForMaterial", xml.OP_EQUALS, true);
			let viewRefId;

			if (this._isColorRuleEnforcedOrEnabled(ctx)) {
				viewRefId = "ColorInPalettes";
				this._applyStyleHierarchyFilters(ctx, q, "Child:__Parent__");
			} else {
				viewRefId = "ProductColorSpecs";
				q.Attr("Active", xml.OP_EQUALS, true);
			}

			return lang.mixin(new Action(), {
				m_fixedQuery: q,
				m_viewRef: lang.mixin(new ViewRef(), {
					m_id: viewRefId,
					m_url: "centric://APPAREL/Views"
				})
			});
		},

		/**
		 * Return product from context
		 * @param {Context} ctx
		 * @return {Node}
		 */
		_getProduct: function(ctx) {
			return null;
		},

		/**
		 * Add Season Availability Filters
		 * @param {Context} ctx
		 * @param {Query} query
		 */
		_applySeasonAvailabilityFilters: function(ctx, query) {
			const cache = ctx ? ctx.cache : this.getCache();
			let pm = this._getPartMaterial(ctx);
			const actual = pm && cache.getNode(pm.Actual);
			const spec = actual && cache.getNode(actual.Spec);
			const bom = cache.getParentOfType(pm, ["ApparelBOM"]);
			if (!bom) {
				return;
			}
			// Apply filters only for style BOM
			const isSeasonAvailabilityEnforced = xml.isValidURL(bom.Subtype) && config.getSubtypeSetting(bom.Subtype, "EnforceSeasonAvailability");
			const style = cache.getNode(bom.__Parent__);
			const seasonURL = style && style.ParentSeason;
			if (spec && spec.HasSeasonAvailability && isSeasonAvailabilityEnforced) {
				const seasonAvails = cache.getNodes(spec.SeasonAvailability);
				let seasonAvail = null;
				_.each(seasonAvails, function(node) {
					if (xml.isValidURL(seasonURL) && node.AvailabilitySeason == seasonURL) {
						seasonAvail = node;
					}
				});
				if (seasonAvail) {
					const op = seasonAvail.AvailabilityDefault ? xml.OP_NOT_EQUALS : xml.OP_EQUALS;
					let q = seasonAvail.AvailabilityDefault ? query : query.Or();
					_.each(seasonAvail.AvailabilityColorExceptions, function(url) {
						q.Node(xml.PROP_URL, op, url);
					});
				}
			}
		},

		/**
		 * Get PartMaterial
		 * @param {Context} ctx
		 */
		_getPartMaterial: function(ctx) {
			const cache = ctx ? ctx.cache : this.getCache();
			let pm = ctx ? ctx.getNode() : this.getNode();
			if (pm && pm.$Type == "PartMaterialColor") {
				pm = cache.getNode(pm.__Parent__);
			}
			return pm;
		},

		/**
		 * Check if PartMaterial Actual is a Style
		 * @param {Context} ctx
		 */
		_isPMActualAStyle: function(ctx) {
			const cache = ctx ? ctx.cache : this.getCache();
			let pm = this._getPartMaterial(ctx);
			const actual = pm && cache.getNode(pm.Actual);
			return actual && actual.$Type === "Style";
		},

		/**
		 * Check if the color rule is enabled or enforced for the StyleType
		 * @param {Context} ctx
		 * @return {boolean}
		 */
		_isColorRuleEnforcedOrEnabled(ctx) {
			const rule = this._getColorRule(ctx);
			return rule == "ColorPaletteRuleTypes:Enabled" || rule == "ColorPaletteRuleTypes:Enforced";
		},

		/**
		 * Check if the color rule is enforced for the StyleType
		 * @param {Context} ctx
		 * @return {boolean}
		 */
		_isColorRuleEnforced(ctx) {
			const rule = this._getColorRule(ctx);
			return rule == "ColorPaletteRuleTypes:Enforced";
		},

		/**
		 * Check if the color rule is Off for the StyleType
		 * @param {Context} ctx
		 * @return {boolean}
		 */
		_isColorRuleOff(ctx) {
			const rule = this._getColorRule(ctx);
			return rule == "ColorPaletteRuleTypes:Off";
		},

		/**
		 * Return BomLineItem node, whether node in context is PartMaterialColor or BomLineItem
		 * @param {Context} ctx
		 * @return {Node}
		 */
		_getBOMLineItem: function(ctx) {
			return (ctx.getNodeType() == this.PART_MATERIAL || ctx.getNodeType() == this.FOOD_BOMLINE_ITEM) ? ctx.getNode() : ctx.getNodeParent();
		},
		
		/**
		 * Returns color rule for the StyleType
		 * @param {Context} ctx
		 * @return {string}
		 */
		_getColorRule(ctx) {
			const style = this._getStyle(ctx);
			return style && ProductType.getColorRule(style);
		},

		/**
		 * Get Style
		 * @param {Context} ctx
		 * @return {Node}
		 */
		_getStyle(ctx) {
			const cache = ctx ? ctx.cache : this.getCache();
			let node = ctx ? ctx.getNode() : this.getNode();
			return cache.getParentOfType(node, ["Style"]);
		},

		/**
		 * Apply style hierarchy filters
		 * @param {Context} ctx
		 * @param {Query} query
		 * @param {String} path
		 */
		_applyStyleHierarchyFilters(ctx, query, path) {
			const style = this._getStyle(ctx);
			const cache = ctx ? ctx.cache : this.getCache();
			if (this._isColorRuleEnforcedOrEnabled(ctx)) {
				ProductType.applyStyleHierarchyFilters(style, query, cache, path);
			}
		},

		/**
		 * Apply filters for all types of ProductColorSpec nodes
		 * @param {Context} ctx
		 * @param {Query} query
		 */
		_applyProducColorSpecFilters(ctx, query) {
			let qOr = query.Or();
			if (this._getStyle(ctx) && this._isColorRuleEnforcedOrEnabled(ctx)) {
				let qOrAnd = qOr.And();
				qOrAnd.Node(xml.PROP_TYPE, xml.OP_EQUALS, "ColorInPalette");
				this._applyStyleHierarchyFilters(ctx, qOrAnd, "Child:__Parent__");
			}
			if (!this._isColorRuleEnforced(ctx)) {
				qOr.Node(xml.PROP_TYPE, xml.OP_EQUALS, "ColorSpecification");
				qOr.Node(xml.PROP_TYPE, xml.OP_EQUALS, "PrintDesignColor");
			}
			query.Attr(this._isPMActualAStyle(ctx) ? "OKForStyle" : "OKForMaterial", xml.OP_EQUALS, true);
			query.Attr("Active", xml.OP_EQUALS, true);
		}

	});
});
